import React, { useState, useEffect } from "react";
import api from "../../api/axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Plus, Eye } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatCurrencyCOP, formatDate } from "../../utils/format";
import LoadingOverlay from "../../components/LoadingOverlay";
import Pagination from "../../components/Pagination";

function Exits() {
  const [exits, setExits] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const ITEMS_PER_PAGE = 50;
  const [currentPage, setCurrentPage] = useState(0);

  // Filtros
  const [selectedClient, setSelectedClient] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [filteredExits, setFilteredExits] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchExits();
    fetchClients();

    if (location.state?.successMessage) {
      setTimeout(() => {
        toast.success(location.state.successMessage);
      }, 300);
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Reinicia página cuando cambien filtros
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedClient, dateFrom, dateTo]);

  const fetchExits = async () => {
    try {
      setLoading(true);
      const res = await api.get("/exits");
      setExits(res.data || []);
      setFilteredExits(res.data || []);
    } catch (error) {
      toast.error("Error al cargar las salidas");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = () => {
    api
      .get("/clients")
      .then((res) => setClients(res.data))
      .catch(() => toast.error("Error al cargar clientes"));
  };

  // Aplicar filtros
  const applyFilters = () => {
    let filtered = exits;

    if (selectedClient) {
      filtered = filtered.filter(
        (exit) => exit.client?.id === Number(selectedClient),
      );
    }

    if (dateFrom) {
      filtered = filtered.filter((exit) => exit.date >= dateFrom);
    }

    if (dateTo) {
      filtered = filtered.filter((exit) => exit.date <= dateTo);
    }

    setFilteredExits(filtered);
  };

  // Limpiar filtros
  const clearFilters = () => {
    setSelectedClient("");
    setDateFrom("");
    setDateTo("");
    setFilteredExits(exits);
  };

  // PAGINACIÓN
  const pageCount = Math.ceil(filteredExits.length / ITEMS_PER_PAGE);

  const currentItems = filteredExits.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE,
  );

  return (
    <div className="bg-white p-6 rounded-xl shadow-md relative">
      <LoadingOverlay loading={loading} />

      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-semibold text-gray-800">Salidas</h2>

        <Link
          to="/exits/create"
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow flex items-center gap-2 transition duration-300"
        >
          <Plus size={18} />
          Nueva Salida
        </Link>
      </div>

      {/* FILTROS */}
      <div className="bg-gray-50 p-4 rounded-lg shadow mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Fecha Inicial */}
        <div>
          <label className="block text-sm font-semibold">Fecha Inicial</label>
          <input
            type="date"
            className="border p-2 rounded w-full"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>

        {/* Fecha Final */}
        <div>
          <label className="block text-sm font-semibold">Fecha Final</label>
          <input
            type="date"
            className="border p-2 rounded w-full"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        {/* Cliente */}
        <div>
          <label className="block text-sm font-semibold">Cliente</label>
          <select
            className="border p-2 rounded w-full"
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
          >
            <option value="">Todos</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Botones */}
        <div className="flex items-end gap-3">
          <button
            onClick={applyFilters}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg w-full"
          >
            Filtrar
          </button>

          <button
            onClick={clearFilters}
            className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg w-full"
          >
            Limpiar
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto rounded-xl shadow">
        <table className="min-w-full bg-white table-auto">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-center text-sm uppercase tracking-wider">
              <th className="px-6 py-3">Area</th>
              <th className="px-6 py-3">Usuario</th>
              <th className="px-6 py-3">Fecha</th>
              <th className="px-6 py-3">Total Salida</th>
              <th className="px-6 py-3 text-center w-28">Acciones</th>
            </tr>
          </thead>

          <tbody className="text-center">
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-6 text-gray-400 italic">
                  No hay registros.
                </td>
              </tr>
            ) : (
              currentItems.map((exit, index) => (
                <tr
                  key={exit.id}
                  className={`border-t ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                >
                  <td className="px-6 py-4">{exit.client?.name}</td>
                  <td className="px-6 py-4">
                    {exit.client?.users?.[0]?.name || "Sin usuario"}
                  </td>{" "}
                  <td className="px-6 py-4">{formatDate(exit.date)}</td>
                  <td className="px-6 py-4">
                    {formatCurrencyCOP(exit.total_exit)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center">
                      <Link
                        to={`/exits/${exit.id}`}
                        className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg shadow transition-all duration-300"
                        title="Ver Detalles"
                      >
                        <Eye size={16} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINACIÓN */}
      <Pagination
        pageCount={pageCount}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        currentItemsCount={currentItems.length}
        totalItemsCount={filteredExits.length}
      />
    </div>
  );
}

export default Exits;
